import Alert from "@/common/components/Alert";
import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextArea from "@/common/components/TextArea";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities } from "@/common/constants/unities.constant";
import useCompany from "@/common/hooks/queries/useCompany";
import useCompanyEquipment from "@/common/hooks/queries/useCompanyEquipment";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useCreateMaintenanceWorkflow } from "@/features/privateCompanies/hooks/maintenance/workflows/useCreateMaintenanceWorkflow";
import { maintenanceWorkflowSchema, MaintenanceWorkflowValidationSchema } from "@/features/privateCompanies/schemas/maintenance/workflows/maintenanceWorkflow.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AlertCircleIcon, ChevronsRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import EquipmentData from "./components/EquipmentData";
import MaintenanceWorkflowSelectSolicitations from "./components/MaintenanceWorkflowSelectSolicitations";

const CompanyMaintenanceWorkflowsCreate = () => {
  const { companyId } = useParams();

  const queryClient = useQueryClient();

  const goBack = useGoBack();

  const { filterParams } = useFilterParams();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: company,
    isPending: companyIsPending,
  } = useCompany({
    id: companyId || '',
  });

  const {
    data: equipment,
    isError: equipmentIsError,
    error: equipmentError,
  } = useCompanyEquipment({
    id: filterParams.equipment,
    options: {
      retry: false,
    },
  });

  const formMethods = useForm<MaintenanceWorkflowValidationSchema>({
    resolver: zodResolver(maintenanceWorkflowSchema),
    defaultValues: {
      solicitation_ids: [],
    }
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: {
      errors,
    },
  } = formMethods;

  const {
    mutate: createMaintenanceWorkflow,
    isPending: createMaintenanceWorkflowIsPending,
  } = useCreateMaintenanceWorkflow({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflows', companyId] });

      goBack({
        fallback: `/companies/${companyId}/maintenance/workflows`,
        searchLastRouteFromFallback: true,
      });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
      })(error);
    },
  });

  const onSubmit: SubmitHandler<MaintenanceWorkflowValidationSchema> = (data) => createMaintenanceWorkflow(data);

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasPermission = hasUserPermission('Admin::Maintenance::Workflows::Create');

  useEffect(() => {
    if (!filterParams.equipment || equipmentError?.code === AxiosError.ERR_BAD_REQUEST) {
      goBack({
        fallback: `/companies/${companyId}/maintenance/workflows`,
        searchLastRouteFromFallback: true,
      });
    }
  }, [equipmentIsError]);

  return (
    <FormProvider {...formMethods}>
      <div className="inner-screen">
        <ShowHeader
          goBack
          goBackProps={{
            fallback: `/companies/${companyId}/maintenance/workflows`,
            searchLastRouteFromFallback: true,
          }}
          isLoading={companyIsPending}
          title={company?.general_information?.fantasy_name}
          subtitle={(
            <div className="flex items-center gap-2">
              <p>
                Processos de manutenção (OS)
              </p>

              <ChevronsRightIcon />

              <p className="text-white-800">
                Criação rápida
              </p>
            </div>
          )}
          titleClassName="text-md"
          className="text-white-400"
          subtitleClassName="text-nowrap font-medium"
          buttons={hasPermission && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                disabled={createMaintenanceWorkflowIsPending}
                onClick={() => {
                  goBack({
                    fallback: `/companies/${companyId}/maintenance/workflows`,
                    searchLastRouteFromFallback: true,
                  });
                }}
              >
                Cancelar
              </Button>

              <Button
                onClick={handleSubmit(onSubmit)}
                isLoading={createMaintenanceWorkflowIsPending}
              >
                Gerar processo
              </Button>
            </div>
          )}
        />

        <ErrorMessageWrapper
          statusCode={isUserPermissionsPending || hasPermission ? 200 : 403}
          className="flex-1"
        >
          <Box className="flex-1 flex flex-col gap-4 overflow-auto">
            <EquipmentData
              equipment={equipment}
            />

            <div className="flex gap-4">
              <TextArea
                label="Descrição geral do processo"
                markAsRequired
                className="flex-1"
                textareaClassName="text-xs"
                rows={2}
                {...register('observations')}
                errorMessage={errors.observations?.message}
              />

              <UnitTextField
                label="Odômetro/Horímetro atual do equipamento"
                markAsRequired
                decimals={0}
                unit={equipment?.model?.category?.unit_for_consumption && unities[equipment?.model?.category?.unit_for_consumption]}
                labelClass="h-[86px]"
                valueClassName="h-full flex-1"
                {...register('current_equipment_usage')}
                errorMessage={errors.current_equipment_usage?.message}
              />
            </div>

            <Alert
              variant="warning"
              Icon={AlertCircleIcon}
              containerClassName="bg-transparent p-0"
            >
              <div className="flex flex-col">
                <p className="text-xs">
                  Você só poderá gerar um processo de manutenção com ao menos 1 solicitação selecionada.
                </p>

                <p className="text-xs italic font-light">
                  Selecione os programas que você deseja adiantar e as solicitações que deseja unir em um processo de manutenção
                </p>
              </div>
            </Alert>

            {errors.solicitation_ids?.message && (
              <ErrorMessage
                name="solicitation_ids"
                message={errors.solicitation_ids?.message}
              />
            )}

            <MaintenanceWorkflowSelectSolicitations />
          </Box>
        </ErrorMessageWrapper>
      </div>

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </FormProvider>
  );
}

export default CompanyMaintenanceWorkflowsCreate;
